<template>
  <SSection
    :title="item?.name"
    title-tag="h1"
    :has-separator="false"
    :desktop-full="true"
  >
    <TText tag="h2" classes="mb-0">
      {{ item?.village.name }}, {{ item?.province.name }}
    </TText>

    <div v-if="item.isReviewsEnabled" class="inline-flex items-center mt-1">
      <TStars :stars="item?.review?.averageReviews" />
      <TText class="ml-1" classes="mb-0" color="gray600"
        >({{ item?.review?.totalReviews }})</TText
      >
    </div>
    <div class="grid gap-x-5 grid-cols-2 w-fit sm:flex sm:gap-x-8 mt-4">
      <div class="flex items-center m-1">
        <div
          v-if="item.rentType === 'FULL' || item.rentType === 'BOTH'"
          class="mr-1 flex items-center justify-center"
        >
          <UIcon
            :name="'i-heroicons-home'"
            class="w-5 h-5"
            aria-hidden="true"
          />
        </div>
        <div
          v-if="item.rentType === 'ROOMS' || item.rentType === 'BOTH'"
          class="mr-1 flex items-center justify-center"
        >
          <UIcon
            :name="'i-fluent-conference-room-20-regular'"
            class="w-5 h-5"
            aria-hidden="true"
          />
        </div>
        <div>
          <TText size="xs" leading="none" classes="mb-0">
            {{
              (item.rentType === 'FULL' &&
                t('views.cottage.visualInformation.rentTypeFull')) ||
              (item.rentType === 'ROOMS' &&
                t('views.cottage.visualInformation.rentTypeRooms')) ||
              (item.rentType === 'BOTH' &&
                t('views.cottage.visualInformation.rentTypeBoth'))
            }}</TText
          >
        </div>
      </div>
      <div class="flex items-center m-1">
        <div class="mr-1 flex items-center justify-center">
          <UIcon
            :name="'i-heroicons-user'"
            class="w-5 h-5"
            aria-hidden="true"
          />
        </div>
        <div>
          <TText size="xs" leading="none" classes="mb-0">
            {{
              item?.capacity.min
                ? `${item?.capacity.min} - ${item?.capacity.max} ${t(
                    'views.cottage.visualInformation.capacity',
                  )}`
                : `${item?.capacity.max} ${t(
                    'views.cottage.visualInformation.capacity',
                  )}`
            }}
          </TText>
        </div>
      </div>
      <div class="flex items-center m-1">
        <div class="mr-1 flex items-center justify-center">
          <UIcon
            :name="'i-fluent-door-tag-20-regular'"
            class="w-5 h-5"
            aria-hidden="true"
          />
        </div>
        <div>
          <TText size="xs" leading="none" classes="mb-0">
            {{
              item.numRooms > 1
                ? t('views.cottage.visualInformation.numRooms', item.numRooms)
                : t('views.cottage.visualInformation.room')
            }}</TText
          >
        </div>
      </div>
      <div class="flex items-center m-1">
        <div class="mr-1 flex items-center justify-center">
          <UIcon
            :name="'i-fluent-bed-24-regular'"
            class="w-5 h-5"
            aria-hidden="true"
          />
        </div>
        <div>
          <TText size="xs" leading="none" classes="mb-0">
            {{
              item.numBeds > 1
                ? t('views.cottage.visualInformation.numBeds', item.numBeds)
                : t('views.cottage.visualInformation.bed')
            }}</TText
          >
        </div>
      </div>
    </div>
    <div>
      <UBadge
        v-if="item.salesService.isOnlineBookingActive"
        data-test="onlineBookingBadge"
        class="mt-4"
        color="blue"
        size="md"
        >{{ t('views.cottage.tags.online-booking') }}</UBadge
      >
    </div>
    <UButton
      icon="i-heroicons-arrow-right"
      data-test="toPricesButton"
      size="lg"
      color="primary"
      variant="link"
      class="mt-6"
      :padded="false"
      :label="t('views.cottage.visualInformation.toPrices')"
      trailing
      @click="toPricesPage()"
    />
  </SSection>
</template>

<script setup lang="ts">
import type { CottagePage } from '../../entities/CottagePage'
import { sendGa4CottagePricesLink } from '~/modules/integrations/ga4/composables/useAnalyticsGa4Composable'

type itemType = Pick<
  CottagePage,
  | 'slug'
  | 'rentType'
  | 'capacity'
  | 'numRooms'
  | 'numBeds'
  | 'name'
  | 'review'
  | 'isReviewsEnabled'
> & {
  village: Pick<CottagePage['village'], 'name'>
} & {
  province: Pick<CottagePage['province'], 'name' | 'slug'>
} & {
  salesService: Pick<CottagePage['salesService'], 'isOnlineBookingActive'>
}

interface propsInterface {
  item: itemType
}

const props = defineProps<propsInterface>()

const route = useRoute()

const { dateIn, dateOut, capacity, cos } = route.query

const toPricesPage = () => {
  sendGa4CottagePricesLink({
    cottage: {
      name: props.item.name,
    },
  })
  navigateTo(
    routes().prices({
      province: props.item.province.slug,
      slug: props.item.slug,
      dateIn: typeof dateIn === 'string' ? dateIn : undefined,
      dateOut: typeof dateOut === 'string' ? dateOut : undefined,
      capacity: typeof capacity === 'string' ? capacity : undefined,
      cos: typeof cos === 'string' ? cos : undefined,
    }),
    {
      external: true,
    },
  )
}
</script>
